import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, MessageSquare, Shield } from 'lucide-react';

// Phase components
import ChatInterface        from './components/ChatInterface';
import CallbackTransition   from './components/CallbackTransition';
import VoiceCallbackPanel   from './components/VoiceCallbackPanel';
import AgentAssistDesktop   from './components/AgentAssistDesktop';

// ─── Component ────────────────────────────────────────────────────────────────

const CustomerServiceDashboardWithBot = () => {
  const navigate = useNavigate();

  // ── Phase & transcript ────────────────────────────────────────────────────
  const [phase, setPhase]                     = useState('web_chat');
  const [chatHistorySnapshot, setChatHistorySnapshot] = useState('');

  const transcriptRef = useRef('');

  // ── Transcript update (called by child phases) ───────────────────────────

  const handleTranscriptUpdate = useCallback((text) => {
    transcriptRef.current = text;
  }, []);

  // ── Phase transitions ─────────────────────────────────────────────────────

  const handleRequestCallback      = useCallback(() => setPhase('callback_transition'), []);
  const handleCallbackComplete     = useCallback(() => {
    setChatHistorySnapshot(transcriptRef.current); // snapshot web chat before voice overwrites
    setPhase('voice_callback');
  }, []);
  const handleTransferToSpecialist = useCallback(() => setPhase('fraud_specialist'), []);


  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 via-[#1a3a6b]/5 to-[#1a3a6b]/[0.03] flex flex-col overflow-hidden">

      {/* ── Header ── */}
      <div className="relative px-6 pt-6 pb-0 flex-shrink-0">
        <div className="relative bg-white/90 backdrop-blur-xl p-4 sm:p-5 rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a3a6b]/5 to-[#1a3a6b]/10 opacity-70 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-3">

            {/* Left: back + title */}
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/home')}
                className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-xl transition-all duration-200 flex-shrink-0"
                aria-label="Back to home"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Customer Service Claim Report
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                  CAP Credit Union · Omnichannel AI Experience
                </p>
              </div>
            </div>

            {/* Right: logos */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-[#1a3a6b]" />
                <span className="text-xs font-bold text-[#1a3a6b]">CAP Credit Union</span>
              </div>
              <div className="w-px h-6 bg-gray-200" />
              <img src="/capgemini-logo.png" alt="Capgemini" className="h-5 object-contain" />
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden mt-4 mx-6 mb-6 gap-4 min-h-0">

        {/* Phase 1: Web Chat */}
        {phase === 'web_chat' && (
          <>
            {/* Left: chat */}
            <div className="w-[42%] flex flex-col min-h-0">
              <ChatInterface
                onRequestCallback={handleRequestCallback}
                onTranscriptUpdate={handleTranscriptUpdate}
              />
            </div>

            {/* Right: intro / context panel */}
            <div className="flex-1 flex flex-col gap-4 min-h-0 overflow-y-auto">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center flex-1">
                <div className="w-16 h-16 rounded-full bg-[#1a3a6b]/10 flex items-center justify-center mb-4">
                  <MessageSquare className="w-8 h-8 text-[#1a3a6b]" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Authenticated Web Chat</h2>
                <p className="text-gray-500 text-sm max-w-md leading-relaxed">
                  Member Jamie Wallace is signed in to secure online banking. The AI assistant
                  verifies identity, retrieves account balances and transactions, then offers
                  a seamless voice callback when the member needs to step away.
                </p>
                <div className="mt-6 grid grid-cols-3 gap-3 w-full max-w-sm">
                  {[
                    { label: 'Channel',    value: 'Authenticated Web Chat' },
                    { label: 'Auth',       value: 'Phone last-4 + OTP'     },
                    { label: 'Escalation', value: 'Voice Callback'          },
                  ].map(item => (
                    <div key={item.label} className="bg-gray-50 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-700">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Phase 2: Callback Transition */}
        {phase === 'callback_transition' && (
          <div className="w-full flex flex-col min-h-0">
            <CallbackTransition onComplete={handleCallbackComplete} />
          </div>
        )}

        {/* Phase 3: Voice Callback */}
        {phase === 'voice_callback' && (
          <div className="flex-1 flex flex-col min-h-0">
            <VoiceCallbackPanel
              chatHistory={chatHistorySnapshot}
              onTransferToSpecialist={handleTransferToSpecialist}
              onTranscriptUpdate={handleTranscriptUpdate}
              onCallStatusChange={() => {}}
            />
          </div>
        )}

        {/* Phase 4: Fraud Specialist */}
        {phase === 'fraud_specialist' && (
          <div className="flex-1 flex flex-col min-h-0">
            <AgentAssistDesktop onTranscriptUpdate={handleTranscriptUpdate} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerServiceDashboardWithBot;
